import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { PagedResult } from '../../administration/users/userTypes';
import type {
  AttritionRequestDetail,
  AttritionRequestListItem,
  AttritionRequestListQuery,
} from '../../human-resources/attrition/attritionTypes';

/**
 * The employee's own attrition requests — `api/self-service/attrition-requests`, scoped by the
 * ROUTE to the caller's own personnel record. The query has no scope field to widen it with.
 */
function toQueryString(query: Omit<AttritionRequestListQuery, 'awaitingMe'>): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Present-but-empty is "ticked to nothing", which matches no row — so `!== undefined`.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.category !== undefined) params.set('category', query.category);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const BASE = '/api/self-service/attrition-requests';

export const myAttritionRequestsApi = {
  list: (query: Omit<AttritionRequestListQuery, 'awaitingMe'>, signal?: AbortSignal) =>
    api<PagedResult<AttritionRequestListItem>>(`${BASE}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<AttritionRequestDetail>(`${BASE}/${id}`, { signal }),

  /** Submits, or replaces before approval, one document of the caller's own request. Real progress. */
  uploadDocument: (id: string, lineId: string, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<AttritionRequestDetail>(`${BASE}/${id}/documents/${lineId}`, form, { onProgress });
  },

  /** A FETCH then an object URL, never an `<a href>` — the token travels in a header no navigation sends. */
  downloadDocument: (id: string, lineId: string, signal?: AbortSignal) =>
    apiBlob(`${BASE}/${id}/documents/${lineId}/file`, { signal }),
};
